import React from 'react';
import { useShop } from '../context/ShopContext';
import { 
  X, 
  ShieldCheck, 
  FileText, 
  RefreshCw, 
  Truck, 
  Building2, 
  Printer, 
  ExternalLink,
  MessageCircle,
  Phone,
  Mail,
  Award
} from 'lucide-react';

export default function PolicyModal() {
  const { policyModal, closePolicyModal, openPolicyModal, storeSettings } = useShop();

  if (!policyModal || !policyModal.isOpen) return null;

  const currentType = policyModal.type || 'privacy';

  const tabs = [
    { id: 'privacy', label: 'Privacy Policy', icon: <ShieldCheck size={15} /> },
    { id: 'terms', label: 'Terms & Conditions', icon: <FileText size={15} /> },
    { id: 'return', label: 'Lifetime Exchange & Returns', icon: <RefreshCw size={15} /> },
    { id: 'shipping', label: 'Insured Armored Transit', icon: <Truck size={15} /> },
    { id: 'about', label: 'Brand Heritage Story', icon: <Award size={15} /> },
  ];

  const getPolicyContent = () => {
    switch (currentType) {
      case 'privacy':
        return {
          title: 'Privacy & Data Protection Policy',
          subtitle: `How ${storeSettings.storeName} guards your identity, insured logistics, and financial privacy.`,
          content: storeSettings.privacyPolicy || 'No privacy policy configured yet.',
          tag: 'Confidentiality Assured'
        };
      case 'terms':
        return {
          title: 'Terms of Sale & Bullion Conditions',
          subtitle: `Bullion market rate indexing, BIS hallmarking certification, and order conditions.`,
          content: storeSettings.termsConditions || 'No terms and conditions configured yet.',
          tag: 'Official Sales Agreement'
        };
      case 'return':
        return {
          title: '15-Day Inspection & Lifetime Exchange',
          subtitle: `Our hallmark 100% buyback promise on pure gold and certified diamonds.`,
          content: storeSettings.returnPolicy || 'No return policy configured yet.',
          tag: '100% Lifetime Promise'
        };
      case 'shipping':
        return {
          title: 'Insured Armored Transit & Delivery Policy',
          subtitle: `Tamper-evident sealed security vaults, armored escort carriers, and transit insurance.`,
          content: storeSettings.shippingPolicy || 'No shipping policy configured yet.',
          tag: 'Zero-Risk Transit'
        };
      case 'about':
        return {
          title: `About ${storeSettings.storeName}`,
          subtitle: storeSettings.tagline || 'Master jewellery purveyors and hallmark artisans.',
          content: storeSettings.aboutStory || `${storeSettings.storeName} is a premier luxury jewellery house renowned for certified purity and bespoke bridal masterpieces.`,
          tag: 'Royal Heritage Legacy'
        };
      default:
        return {
          title: 'Store Policies',
          subtitle: 'Official documentation and customer assurances.',
          content: '',
          tag: 'Compliance'
        };
    }
  };

  const activeData = getPolicyContent();

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(5, 5, 10, 0.88)',
      backdropFilter: 'blur(10px)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: '#11111a',
        border: '1px solid var(--border-gold)',
        borderRadius: 'var(--radius-lg)',
        width: '100%',
        maxWidth: '780px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 60px rgba(0, 0, 0, 0.85)',
        overflow: 'hidden'
      }}>
        
        {/* Header Bar */}
        <div style={{
          padding: '1.2rem 1.6rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          background: 'linear-gradient(to right, #161624, #11111a)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem'
        }}>
          <div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              color: 'var(--gold-400)',
              fontSize: '0.72rem',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.08em'
            }}>
              <span>{storeSettings.storeName}</span>
              <span>•</span>
              <span>{activeData.tag}</span>
            </div>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', margin: '0.2rem 0 0' }}>
              {activeData.title}
            </h3>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0.15rem 0 0' }}>
              {activeData.subtitle}
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              onClick={() => window.print()}
              className="btn-icon"
              style={{ width: '34px', height: '34px', color: 'var(--gold-400)' }}
              title="Print Policy Document"
            >
              <Printer size={16} />
            </button>
            <button
              onClick={closePolicyModal}
              className="btn-icon"
              style={{ width: '34px', height: '34px' }}
              title="Close"
            >
              <X size={17} />
            </button>
          </div>
        </div>

        {/* Policy Tab Switcher */}
        <div style={{
          display: 'flex',
          overflowX: 'auto',
          padding: '0.6rem 1.2rem',
          background: '#0d0d14',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          gap: '0.4rem'
        }} className="hide-scrollbar">
          {tabs.map((tab) => {
            const isActive = currentType === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => openPolicyModal(tab.id)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  padding: '0.4rem 0.85rem',
                  borderRadius: 'var(--radius-full)',
                  border: isActive ? '1px solid var(--gold-400)' : '1px solid transparent',
                  background: isActive ? 'rgba(212, 175, 55, 0.15)' : 'transparent',
                  color: isActive ? 'var(--gold-300)' : 'var(--text-muted)',
                  fontSize: '0.78rem',
                  fontWeight: isActive ? 700 : 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'var(--transition)'
                }}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Policy Text Body */}
        <div style={{
          padding: '1.6rem',
          overflowY: 'auto',
          flex: 1,
          color: '#d4d4dc',
          fontSize: '0.88rem',
          lineHeight: '1.7',
          whiteSpace: 'pre-line'
        }}>
          {activeData.content}

          {/* Official Verification & Registration Pillar */}
          <div style={{
            marginTop: '2rem',
            paddingTop: '1.2rem',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1rem',
            background: 'rgba(212, 175, 55, 0.04)',
            padding: '1rem',
            borderRadius: 'var(--radius-sm)'
          }}>
            <div>
              <div style={{ fontSize: '0.72rem', color: 'var(--gold-400)', fontWeight: 700, textTransform: 'uppercase' }}>
                BIS Hallmark License No.
              </div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff' }}>
                {storeSettings.bisLicense || 'HM/C-7689123490'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', color: 'var(--gold-400)', fontWeight: 700, textTransform: 'uppercase' }}>
                Statutory GSTIN
              </div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff' }}>
                {storeSettings.gstin || '08AAAFM1234F1Z8'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', color: 'var(--gold-400)', fontWeight: 700, textTransform: 'uppercase' }}>
                Flagship Boutique
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                {storeSettings.address}
              </div>
            </div>
          </div>
        </div>

        {/* Footer with Concierge Help */}
        <div style={{
          padding: '1rem 1.6rem',
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          background: '#0d0d14',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.2rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
              <Phone size={13} color="var(--gold-400)" /> {storeSettings.phone}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
              <Mail size={13} color="var(--gold-400)" /> {storeSettings.email}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            {storeSettings.whatsapp && (
              <a
                href={`https://wa.me/${String(storeSettings.whatsapp).replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Namaste, I am reading the ${activeData.title} and have an inquiry regarding ${storeSettings.storeName}.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-glass btn-sm"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  fontSize: '0.78rem',
                  color: '#34d399',
                  borderColor: 'rgba(52, 211, 153, 0.3)'
                }}
              >
                <MessageCircle size={13} /> WhatsApp Desk
              </a>
            )}
            <button
              onClick={closePolicyModal}
              className="btn btn-gold btn-sm"
              style={{ fontSize: '0.8rem', padding: '0.4rem 1rem' }}
            >
              Understood
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
